import { useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

const FALLBACK_FAVICON = '/favicon.png';

function setFavicons(url: string) {
  // Remove ALL existing favicon links first
  const existingLinks = document.querySelectorAll(
    'link[rel="icon"], link[rel="shortcut icon"], link[rel="apple-touch-icon"]'
  );
  existingLinks.forEach(link => link.remove());

  // Add cache-busting parameter
  const cacheBustedUrl = `${url}${url.includes('?') ? '&' : '?'}v=${Date.now()}`;

  // Create main favicon link
  const iconLink = document.createElement("link");
  iconLink.rel = "icon";
  iconLink.type = "image/png";
  iconLink.href = cacheBustedUrl;
  document.head.appendChild(iconLink);

  // Create shortcut icon for legacy browsers
  const shortcutLink = document.createElement("link");
  shortcutLink.rel = "shortcut icon";
  shortcutLink.href = cacheBustedUrl;
  document.head.appendChild(shortcutLink);

  // Create apple-touch-icon
  const appleTouchIcon = document.createElement("link");
  appleTouchIcon.rel = "apple-touch-icon";
  appleTouchIcon.href = cacheBustedUrl;
  document.head.appendChild(appleTouchIcon);
}

export function FaviconLoader() {
  const hasInitialized = useRef(false);

  // Set fallback favicon immediately on first render
  useEffect(() => {
    if (!hasInitialized.current) {
      setFavicons(FALLBACK_FAVICON);
      hasInitialized.current = true;
    }
  }, []);

  const { data: activeFavicon } = useQuery({
    queryKey: ["active-favicon"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("favicons")
        .select("image_url")
        .eq("is_active", true)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    staleTime: 1000 * 60 * 5,
  });

  // Update favicon when data loads from database
  useEffect(() => {
    if (activeFavicon?.image_url) {
      setFavicons(activeFavicon.image_url);
    }
  }, [activeFavicon?.image_url]);

  return null;
}
